import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, TrendingUp, TrendingDown, 
    ArrowLeftRight, FileSpreadsheet, LogOut, FileClock, Users, Moon, Sun, Languages
} from 'lucide-react';

export default function TenantLayout({ children, title, tagline }) {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { language, toggleLanguage, t } = useLanguage();
    const navigate = useNavigate();
    const { clientSlug } = useParams();
    const location = useLocation();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const navItems = [
        { name: t('Dashboard'), path: `/${clientSlug}`, icon: LayoutDashboard },
        { name: t('Income'), path: `/${clientSlug}/income`, icon: TrendingUp },
        { name: t('Expense'), path: `/${clientSlug}/expense`, icon: TrendingDown },
        { name: t('Directory'), path: `/${clientSlug}/parties`, icon: Users },
        { name: t('Payments'), path: `/${clientSlug}/payments`, icon: ArrowLeftRight },
        { name: t('Reports'), path: `/${clientSlug}/reports`, icon: FileSpreadsheet },
        { name: t('Audit Trail'), path: `/${clientSlug}/audit`, icon: FileClock },
    ];

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    return (
        <div className={`min-h-screen flex flex-col ${theme === 'dark' ? 'bg-slate-900 text-slate-100' : 'bg-[#F4F7FB] text-slate-900'}`}>
            {/* Top Navigation Bar */}
            <header className={`h-16 sm:h-20 border-b flex items-center justify-between px-4 sm:px-8 xl:px-24 sticky top-0 z-50 transition-colors ${theme === 'dark' ? 'bg-slate-950 border-slate-800' : 'bg-[#0A192F] border-[#1D4B88] text-white'}`}>
                <div className="flex items-center gap-4 sm:gap-6 flex-1">
                    {/* Hamburger Menu for Mobile */}
                    <button 
                        data-testid="mobile-menu-toggle"
                        className="md:hidden p-2 -ml-2 rounded-lg hover:bg-white/10 min-h-[44px] min-w-[44px] flex flex-col justify-center items-center"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        <div className="space-y-1.5 w-6">
                            <span className="block w-full h-0.5 bg-current"></span>
                            <span className="block w-full h-0.5 bg-current"></span>
                            <span className="block w-full h-0.5 bg-current"></span>
                        </div>
                    </button>

                    <Link to={`/${clientSlug}`} className="flex items-center gap-3 group">
                        {user?.logo_url ? (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg overflow-hidden border-2 border-[#EAB308] group-hover:scale-105 transition-transform bg-white">
                                <img src={user.logo_url} alt="Logo" className="w-full h-full object-contain" />
                            </div>
                        ) : (
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-bold group-hover:scale-105 transition-transform ${theme === 'dark' ? 'bg-amber-500 text-slate-950' : 'bg-[#EAB308] text-[#0A192F]'}`}>
                                {user?.business_name ? user.business_name.substring(0, 2).toUpperCase() : 'RC'}
                            </div>
                        )}
                        <div className="hidden sm:block">
                            <div className="text-sm font-bold tracking-wider" data-testid="tenant-brand-header-badge">
                                {user?.business_name || clientSlug.toUpperCase()}
                            </div>
                            <div className={`text-xs font-mono ${theme === 'dark' ? 'text-slate-400' : 'text-slate-400'}`}>rccompany.in/{clientSlug}</div>
                        </div>
                    </Link>
                </div>

                {/* Desktop Navigation - Centered perfectly */}
                <div className={`hidden lg:flex flex-1 justify-center`}>
                    <div className={`flex h-10 items-center rounded-lg p-1 border ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-[#163868] border-[#1D4B88]'}`}>
                        {navItems.map((item) => (
                            <Link 
                                key={item.path}
                                to={item.path}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                                    location.pathname === item.path 
                                    ? (theme === 'dark' ? 'bg-amber-500 text-slate-950' : 'bg-[#EAB308] text-[#0A192F]') 
                                    : (theme === 'dark' ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-[#1D4B88]')
                                }`}
                            >
                                <item.icon size={16} />
                                <span className="hidden xl:inline">{item.name}</span>
                                <span className="xl:hidden" title={item.name}>{item.name.includes(' ') ? item.name.split(' ')[0] : item.name}</span>
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 justify-end flex-1 flex-shrink-0">
                    <button 
                        onClick={toggleLanguage}
                        data-testid="language-toggle"
                        className={`flex items-center justify-center gap-1 px-3 min-h-[44px] min-w-[44px] rounded-lg text-sm font-medium transition-colors ${theme === 'dark' ? 'text-slate-300 hover:text-amber-400 hover:bg-slate-800 bg-slate-900/50' : 'text-slate-200 hover:text-amber-400 hover:bg-white/10 bg-[#163868]'}`}
                        title="Toggle Language"
                    >
                        <Languages size={16} />
                        <span className="hidden sm:inline">{language === 'en' ? 'EN' : 'ગુજ'}</span>
                        <span className="sm:hidden">{language === 'en' ? 'EN' : 'GJ'}</span>
                    </button>
                    <button 
                        onClick={toggleTheme}
                        data-testid="theme-toggle"
                        className={`min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg transition-colors ${theme === 'dark' ? 'text-slate-400 hover:text-amber-400 hover:bg-slate-800' : 'text-slate-300 hover:text-amber-400 hover:bg-white/10'}`}
                        title="Toggle Theme"
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                    <Link to={`/${clientSlug}/settings`} className="hidden sm:block text-right group" data-testid="tenant-settings-link">
                        <div className={`text-sm font-medium group-hover:text-amber-500 transition-colors ${theme === 'dark' ? 'text-slate-200' : 'text-slate-100'}`}>{user?.name}</div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-amber-500' : 'text-amber-400'}`}>{user?.default_fy || 'FY 24-25'}</div>
                    </Link>
                    <button 
                        onClick={handleLogout}
                        data-testid="tenant-logout-button"
                        className={`p-2 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${theme === 'dark' ? 'text-slate-400 hover:text-rose-400 hover:bg-slate-800' : 'text-slate-300 hover:bg-red-500/20 hover:text-red-400'}`}
                        title="Logout"
                    >
                        <LogOut size={20} />
                    </button>
                </div>
            </header>

            {/* Mobile Nav Overlay */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
                    <div className={`absolute top-16 left-0 w-64 h-[calc(100vh-4rem)] overflow-y-auto ${theme === 'dark' ? 'bg-slate-900' : 'bg-[#0A192F]'} p-4 shadow-xl border-r ${theme === 'dark' ? 'border-slate-800' : 'border-[#1D4B88]'}`} onClick={e => e.stopPropagation()}>
                        <div className="mb-6 pb-4 border-b border-white/10">
                            <div className="text-sm font-bold text-white tracking-wider truncate">
                                {user?.business_name || clientSlug.toUpperCase()}
                            </div>
                            <div className="text-xs font-mono text-slate-400 truncate">rccompany.in/{clientSlug}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                            {navItems.map((item) => (
                                <Link 
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-3 transition-colors ${
                                        location.pathname === item.path 
                                        ? (theme === 'dark' ? 'bg-amber-500 text-slate-950' : 'bg-[#EAB308] text-[#0A192F]') 
                                        : (theme === 'dark' ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-[#1D4B88]')
                                    }`}
                                >
                                    <item.icon size={18} />
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <main className="flex-1 p-4 sm:p-6 sm:py-4 xl:px-24 max-w-[1600px] w-full mx-auto">
                {(title || tagline) && (
                    <div className="mb-4">
                        {title && <h1 className={`text-xl sm:text-2xl font-bold font-['Cabinet_Grotesk'] ${theme === 'dark' ? 'text-slate-100' : 'text-[#0F284E]'}`}>{title}</h1>}
                        {tagline && <p className={`mt-0.5 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{tagline}</p>}
                    </div>
                )}
                {children}
            </main>
        </div>
    );
}
